import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import {
  useGetProductQuery,
  useUpdateProductMutation,
  useGetProductKnowledgeEvaluationsQuery,
  useGetQuizQuestionsQuery
} from './productKnowledgeApiSlice';
import {
  Container,
  Header,
  Form,
  Button,
  Tab,
  Table,
  Icon,
  Loader,
  Message,
  Label,
  Grid,
  Input,
  TextArea
} from 'semantic-ui-react';

// Styling constraints reused from ChatSessionReport/ChatSessionDetail
const innerTabPaneStyle = {
    padding: '24px',
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    border: '1px solid rgba(0,0,0,0.05)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
    marginTop: '0'
};

const labelStyle = { marginBottom: '8px', color: '#1d1d1f', fontWeight: '500' };
const inputStyle = { backgroundColor: '#F5F5F7', borderRadius: '10px', border: 'none', height: '42px', padding: '0 14px' };
const textAreaStyle = { backgroundColor: '#F5F5F7', borderRadius: '10px', border: 'none', padding: '12px 14px' };

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const queryParams = new URLSearchParams(location.search);
  const activeTab = queryParams.get('tab') || 'basic';

  const tabIndexMap = { 'basic': 0, 'evaluations': 1, 'questions': 2 };
  const indexTabMap = { 0: 'basic', 1: 'evaluations', 2: 'questions' };

  useEffect(() => {
    const handleResize = () => {
        setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const { data: product, isLoading, error } = useGetProductQuery(id);
  const [updateProduct, { isLoading: isUpdating }] = useUpdateProductMutation();
  
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    support_url: '',
    parsed_specs: '',
    release_year: '',
    apple_pk: '',
    tech_specs_url: '',
    tech_specs_pk: '',
    is_active: true
  });
  
  const [isDirty, setIsDirty] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        category: product.category || '',
        support_url: product.support_url || '',
        parsed_specs: product.parsed_specs || '',
        release_year: product.release_year || '',
        apple_pk: product.apple_pk || '',
        tech_specs_url: product.tech_specs_url || '',
        tech_specs_pk: product.tech_specs_pk || '',
        is_active: product.is_active ?? true
      });
    }
  }, [product]);

  const handleChange = (e, { name, value, checked }) => {
    const val = name === 'is_active' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: val }));
    setIsDirty(true);
    setSaveSuccess(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    try {
      await updateProduct({ id, ...formData }).unwrap();
      setIsDirty(false);
      setSaveSuccess(true);
      setSaveError(null);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to update product:', err);
      const fieldErrors = err?.data && typeof err.data === 'object'
        ? Object.entries(err.data).map(([field, messages]) => `${field}: ${[].concat(messages).join(' ')}`)
        : [];
      setSaveError(fieldErrors.length ? fieldErrors : ['제품 정보를 저장하지 못했습니다.']);
    }
  };

  const handleTabChange = (e, { activeIndex }) => {
    const tabName = indexTabMap[activeIndex] || 'basic';
    navigate(`?tab=${tabName}`, { replace: true });
  };

  const panes = [
    {
      menuItem: '기본 정보',
      render: () => (
        <Tab.Pane style={innerTabPaneStyle}>
          <Form loading={isLoading || isUpdating} success={saveSuccess} error={!!saveError}>
            {saveSuccess && (
                <Message 
                    success 
                    header="저장됨" 
                    content="제품 정보가 성공적으로 업데이트되었습니다."
                    style={{ borderRadius: '12px', marginBottom: '24px' }}
                />
            )}
            {saveError && (
                <Message
                    error
                    header="저장 실패"
                    list={saveError}
                    style={{ borderRadius: '12px', marginBottom: '24px' }}
                />
            )}
            
            <Header as="h4" dividing style={{ color: '#1d1d1f', marginBottom: '20px' }}>기본 정보</Header>
            <Form.Group widths="equal">
                <Form.Field>
                    <label style={labelStyle}>제품명</label>
                    <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        fluid
                        input={{ style: inputStyle }}
                    />
                </Form.Field>
                <Form.Field>
                    <label style={labelStyle}>카테고리</label>
                    <Input
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        required
                        fluid
                        input={{ style: inputStyle }}
                    />
                </Form.Field>
            </Form.Group>

            <Form.Group widths="equal">
                 <Form.Field>
                    <label style={labelStyle}>출시 연도</label>
                    <Input
                        name="release_year"
                        type="number"
                        value={formData.release_year}
                        onChange={handleChange}
                        fluid
                        input={{ style: inputStyle }}
                    />
                 </Form.Field>
                 <Form.Field>
                    <label style={{ marginBottom: '8px', color: '#1d1d1f', fontWeight: '500' }}>활성 상태</label>
                    <div style={{ 
                        ...inputStyle,
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between'
                    }}>
                        <span style={{ fontSize: '14px', color: formData.is_active ? '#1d1d1f' : '#86868b', fontWeight: '500' }}>
                            {formData.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <Form.Checkbox
                            name="is_active"
                            checked={formData.is_active}
                            onChange={handleChange}
                            toggle
                            style={{ margin: 0, padding: 0 }}
                        />
                    </div>
                 </Form.Field>
            </Form.Group>

            <Header as="h4" dividing style={{ color: '#1d1d1f', marginTop: '30px', marginBottom: '20px' }}>외부 링크 & 키</Header>
            <Form.Group widths="equal">
                <Form.Field>
                    <label style={labelStyle}>지원 URL</label>
                    <Input
                        name="support_url"
                        value={formData.support_url}
                        onChange={handleChange}
                        icon="linkify"
                        iconPosition='left'
                        fluid
                        input={{ style: { ...inputStyle, paddingLeft: '38px' } }}
                    />
                </Form.Field>
                <Form.Field>
                    <label style={labelStyle}>Apple PK</label>
                    <Input
                        name="apple_pk"
                        value={formData.apple_pk}
                        onChange={handleChange}
                        fluid
                        input={{ style: inputStyle }}
                    />
                </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
                <Form.Field>
                    <label style={labelStyle}>Tech Specs URL</label>
                    <Input
                        name="tech_specs_url"
                        value={formData.tech_specs_url}
                        onChange={handleChange}
                        icon="linkify"
                        iconPosition='left'
                        fluid
                        input={{ style: { ...inputStyle, paddingLeft: '38px' } }}
                    />
                </Form.Field>
                <Form.Field>
                    <label style={labelStyle}>Tech Specs PK</label>
                    <Input
                        name="tech_specs_pk"
                        value={formData.tech_specs_pk}
                        onChange={handleChange}
                        fluid
                        input={{ style: inputStyle }}
                    />
                </Form.Field>
            </Form.Group>

            <Header as="h4" dividing style={{ color: '#1d1d1f', marginTop: '30px', marginBottom: '20px' }}>상세 사양 (Parsed Specs)</Header>
            <Form.Field>
                <label style={labelStyle}>파싱된 사양 데이터</label>
                <TextArea
                    name="parsed_specs"
                    value={formData.parsed_specs}
                    onChange={handleChange}
                    rows={10}
                    style={{ ...textAreaStyle, fontFamily: 'monospace', fontSize: '13px', lineHeight: '1.4', resize: 'vertical' }}
                />
            </Form.Field>
            
            <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                    primary 
                    onClick={handleSave} 
                    disabled={!isDirty} 
                    loading={isUpdating}
                    style={{ borderRadius: '10px', height: '40px', padding: '0 24px' }}
                >
                    변경사항 저장
                </Button>
            </div>
          </Form>
        </Tab.Pane>
      )
    },
    {
      menuItem: '제품 지식 평가',
      render: () => (
          <Tab.Pane style={innerTabPaneStyle}>
            <EvaluationsTab productId={id} />
          </Tab.Pane>
      )
    },
    {
      menuItem: '퀴즈 문제',
      render: () => (
          <Tab.Pane style={innerTabPaneStyle}>
            <QuizQuestionsTab productId={id} />
          </Tab.Pane>
      )
    }
  ];

  if (isLoading) return <Loader active />;
  
  if (error) {
     return (
        <Container style={{ padding: '40px' }}>
            <Message error>
                <Message.Header>오류</Message.Header>
                <p>제품 정보를 불러오는데 실패했습니다.</p>
            </Message>
            <Button onClick={() => navigate(-1)}>뒤로 가기</Button>
        </Container>
     ); 
  }

  return (
    <div style={{ height: 'auto', display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#FBFBFD' }}>
      {/* Header Section */}
      <div style={{
          flex: '0 0 auto',
          padding: '16px 0',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0,0,0,0.05)',
          position: 'sticky',
          top: 0,
          zIndex: 100
      }}>
        <Container fluid>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: isMobile ? '0 20px' : '0 40px' }}>
                <Button 
                    icon 
                    basic 
                    onClick={() => navigate('/admin/products')}
                    style={{ boxShadow: 'none', background: 'transparent', padding: '10px', margin: 0 }}
                >
                    <Icon name="arrow left" style={{ color: '#007AFF', fontSize: '18px', margin: 0 }} />
                </Button>
                
                <div style={{ width: '40px', flexShrink: 0, height: '40px', borderRadius: '50%', backgroundColor: '#F2F2F7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon name="box" style={{ margin: 0, color: '#007AFF', fontSize: '18px' }} />
                </div>
                <div>
                    <h2 style={{ fontSize: '17px', fontWeight: '600', margin: 0, color: '#1d1d1f' }}>
                        {product?.name}
                    </h2>
                    <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>
                        {product?.category} {product?.release_year ? `• ${product.release_year}` : ''}
                    </p>
                </div>
            </div>
        </Container>
      </div>

      <Container fluid style={{ padding: isMobile ? '20px' : '40px', flex: 1, maxWidth: '1200px' }}>
        <Tab 
            menu={{ secondary: true, pointing: true, style: { marginBottom: '20px' } }} 
            panes={panes} 
            activeIndex={tabIndexMap[activeTab] || 0}
            onTabChange={handleTabChange}
        />
      </Container>
    </div>
  );
};

const EvaluationsTab = ({ productId }) => {
    const { data: evaluations, isLoading } = useGetProductKnowledgeEvaluationsQuery({ product: productId });
    const navigate = useNavigate();

    if (isLoading) return <Loader active inline="centered" />;
    
    return (
        <Table className="custom-table">
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>날짜</Table.HeaderCell>
                    <Table.HeaderCell>채팅 세션</Table.HeaderCell>
                    <Table.HeaderCell>평가 점수</Table.HeaderCell>
                    <Table.HeaderCell>추출된 주장 (Claim)</Table.HeaderCell>
                    <Table.HeaderCell>평가 결과</Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {evaluations?.results?.length === 0 && (
                    <Table.Row>
                        <Table.Cell colSpan="5" textAlign="center" style={{ padding: '40px', color: '#86868b' }}>
                            이 제품에 대한 평가 기록이 없습니다.
                        </Table.Cell>
                    </Table.Row>
                )}
                {evaluations?.results?.map(evaluation => (
                    <Table.Row key={evaluation.id}>
                        <Table.Cell style={{ color: '#1d1d1f', fontWeight: '500' }}>
                            {new Date(evaluation.created_at).toLocaleDateString()}
                        </Table.Cell>
                        <Table.Cell>
                            <span 
                                onClick={() => navigate(`/chatsessions/${evaluation.chat_session}`)}
                                style={{ color: '#007AFF', cursor: 'pointer', fontWeight: '500' }}
                            >
                                {evaluation.chat_session_str || `Session #${evaluation.chat_session}`}
                            </span>
                        </Table.Cell>
                            <Table.Cell>
                            <Label 
                                size="small"
                                style={{ 
                                    backgroundColor: evaluation.score > 0.7 ? 'rgba(52, 199, 89, 0.15)' : evaluation.score > 0.4 ? 'rgba(255, 149, 0, 0.15)' : 'rgba(255, 59, 48, 0.15)',
                                    color: evaluation.score > 0.7 ? '#34C759' : evaluation.score > 0.4 ? '#FF9500' : '#FF3B30',
                                    borderRadius: '6px',
                                    fontWeight: '600'
                                }}
                            >
                                {(evaluation.score).toFixed(0)}점
                            </Label>
                        </Table.Cell>
                        <Table.Cell>{evaluation.extracted_claims?.length || 0} 개</Table.Cell>
                        <Table.Cell style={{ color: '#86868b' }}>
                            {evaluation.result?.summary ? (
                                evaluation.result.summary.length > 50 
                                ? `${evaluation.result.summary.substring(0, 50)}...` 
                                : evaluation.result.summary
                            ) : (
                                evaluation.result ? JSON.stringify(evaluation.result).substring(0, 50) + "..." : "-"
                            )}
                        </Table.Cell>
                    </Table.Row>
                ))}
            </Table.Body>
        </Table>
    );
}

const QuizQuestionsTab = ({ productId }) => {
    const { data: questions, isLoading } = useGetQuizQuestionsQuery({ product: productId });
    const navigate = useNavigate();
    
    if (isLoading) return <Loader active inline="centered" />;

    return (
        <Table className="custom-table" selectable>
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>질문 내용</Table.HeaderCell>
                    <Table.HeaderCell width={2}>유형</Table.HeaderCell>
                    <Table.HeaderCell width={2} textAlign="center">활성 상태</Table.HeaderCell>
                    <Table.HeaderCell width={3}>채팅 세션</Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {questions?.results?.length === 0 && (
                    <Table.Row>
                            <Table.Cell colSpan="4" textAlign="center" style={{ padding: '40px', color: '#86868b' }}>
                                이 제품에 대한 퀴즈가 없습니다.
                            </Table.Cell>
                    </Table.Row>
                )}
                {questions?.results?.map(question => (
                    <Table.Row key={question.id}>
                        <Table.Cell 
                            style={{ color: '#1d1d1f', fontWeight: '500', cursor: 'pointer' }}
                            onClick={() => navigate(`/admin/quiz-questions/${question.id}`)}
                        >
                            {question.question_text}
                        </Table.Cell>
                        <Table.Cell 
                             onClick={() => navigate(`/admin/quiz-questions/${question.id}`)}
                             style={{ cursor: 'pointer' }}
                        >
                             <Label basic size="tiny">
                                {question.question_type}
                             </Label>
                        </Table.Cell>
                        <Table.Cell 
                            textAlign="center"
                            onClick={() => navigate(`/admin/quiz-questions/${question.id}`)}
                            style={{ cursor: 'pointer' }}
                        >
                            <Icon 
                                name={question.is_active ? 'check circle' : 'ban'} 
                                style={{ color: question.is_active ? '#34C759' : '#C7C7CC' }} 
                            />
                        </Table.Cell>
                        <Table.Cell>
                            {question.chat_session_id ? (
                                <a 
                                    href={`/chatsessions/${question.chat_session_id}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    style={{ color: '#007AFF', textDecoration: 'none' }}
                                    onClick={(e) => e.stopPropagation()} 
                                >
                                     {question.chat_session_str || `Session ${question.chat_session_id}`}
                                </a>
                            ) : (
                                <span style={{ color: '#999' }}>-</span>
                            )}
                        </Table.Cell>
                    </Table.Row>
                ))}
            </Table.Body>
        </Table>
    );
}

export default ProductDetails;
